import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '../utils/streamApi';

const ymd     = d => d.toISOString().slice(0, 10);
const toStart = s => new Date(`${s}T00:00:00`).getTime() / 1000;
const toEnd   = s => new Date(`${s}T23:59:59`).getTime() / 1000;

// Settings worth spotting at a glance in a wall of config noise. Devices first —
// a silenced siren is the one that explains "why didn't the alarm sound?".
const HILITE = {
  DEVICE_PAUSE_UNTIL:      { tag: 'devices', tone: 'warn' },
  ANIMAL_REJECT_CONF:      { tag: 'detection', tone: 'info' },
  STALE_RESET_SECS:        { tag: 'detection', tone: 'info' },
  SMALL_FACE_MIN_PATH_PX:  { tag: 'detection', tone: 'info' },
  SKELETON_NET_RATIO:      { tag: 'detection', tone: 'info' },
  ALERT_COOLDOWN_SECS:     { tag: 'alerts', tone: 'info' },
};

// DEVICE_PAUSE_UNTIL is an epoch — show the wall-clock time it means, since
// "1788039962" tells nobody when the siren came back.
const asEpochTime = v => {
  const n = parseFloat(v);
  if (!isFinite(n)) return null;
  if (n === 0) return 'not paused';
  if (n < 1e9 || n > 4e9) return null;
  try { return new Date(n * 1000).toLocaleString(); } catch { return null; }
};

export default function ConfigChangelogPage() {
  const [rows, setRows]   = useState([]);
  const [start, setStart] = useState(ymd(new Date(Date.now() - 7 * 86400 * 1000)));
  const [end, setEnd]     = useState(ymd(new Date()));
  const [q, setQ]         = useState('');
  const [note, setNote]   = useState('');
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = `n=500&start=${toStart(start)}&end=${toEnd(end)}`
               + (q.trim() ? `&q=${encodeURIComponent(q.trim())}` : '');
      const r = await apiFetch(`/admin/config-changelog?${qs}`);
      const d = r.ok ? await r.json() : { entries: [] };
      setRows(d.entries || []);
      setNote(d.note || '');
      setTruncated(!!d.truncated);
    } finally { setLoading(false); }
  }, [start, end, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto', color: 'var(--text)' }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 24 }}>🧾 Config Changelog</h1>
      <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
        Every configuration change, with its before and after value — cameras, detection
        thresholds, schedules, users, and device pause/resume.
      </p>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', margin: '16px 0' }}>
        <div><div style={lbl}>From</div><input type="date" value={start} onChange={e => setStart(e.target.value)} style={input} /></div>
        <div><div style={lbl}>To</div><input type="date" value={end} onChange={e => setEnd(e.target.value)} style={input} /></div>
        <div style={{ flex: '1 1 220px', minWidth: 180 }}>
          <div style={lbl}>Search key / who / value</div>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="e.g. PAUSE, camera name, username"
                 style={{ ...input, width: '100%', boxSizing: 'border-box' }} />
        </div>
        <button onClick={load} style={btn}>{loading ? 'Loading…' : 'Refresh'}</button>
        <div style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
          {rows.length} change{rows.length === 1 ? '' : 's'}
        </div>
      </div>

      {truncated && (
        <div style={{ marginBottom: 12, padding: '8px 12px', fontSize: 13, borderRadius: 8,
                      background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-muted)' }}>
          Showing the most recent portion of a large changelog — older entries are not included.
        </div>
      )}

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 820 }}>
          <thead><tr style={{ textAlign: 'left', color: 'var(--text-muted)', background: 'var(--surface)' }}>
            {['Time', 'Changed by', 'Setting', 'Before', 'After'].map(h =>
              <th key={h} style={{ padding: '10px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
                {loading ? 'Loading…' : (note || 'No config changes in this range.')}
              </td></tr>
            )}
            {rows.map((r, i) => {
              const hl  = HILITE[r.key];
              const bEp = r.key === 'DEVICE_PAUSE_UNTIL' ? asEpochTime(r.before) : null;
              const aEp = r.key === 'DEVICE_PAUSE_UNTIL' ? asEpochTime(r.after)  : null;
              return (
                <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.time}</td>
                  <td style={td}>{r.source || '—'}</td>
                  <td style={{ ...td, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {hl && <span style={{ ...pill, ...(hl.tone === 'warn' ? pillWarn : pillInfo) }}>{hl.tag}</span>}
                    <code style={{ fontSize: 13 }}>{r.key}</code>
                    {r.action !== 'changed' && (
                      <span style={{ marginLeft: 8, color: 'var(--text-muted)' }}>({r.action})</span>
                    )}
                  </td>
                  <td style={val}>{r.before ?? '—'}{bEp && <div style={sub}>{bEp}</div>}</td>
                  <td style={val}>{r.after ?? '—'}{aEp && <div style={sub}>{aEp}</div>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const input     = { padding: '8px 10px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, fontSize: 14 };
const btn       = { padding: '8px 14px', background: 'var(--surface-alt)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 14 };
const lbl       = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 };
const td        = { padding: '10px 12px' };
const val       = { padding: '10px 12px', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13, wordBreak: 'break-all', maxWidth: 260 };
const sub       = { color: 'var(--text-muted)', fontSize: 12, fontFamily: 'inherit', marginTop: 2 };
const pill      = { display: 'inline-block', padding: '1px 7px', borderRadius: 999, fontSize: 11, marginRight: 8, verticalAlign: 'middle' };
const pillWarn  = { background: 'rgba(234,88,12,0.15)', color: '#ea580c', border: '1px solid rgba(234,88,12,0.35)' };
const pillInfo  = { background: 'var(--surface)', color: 'var(--text-muted)', border: '1px solid var(--border)' };
